import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { Button, Dropdown, OverlayTrigger, Popover, Table, Tooltip } from "react-bootstrap";
import { ApolloError, useReactiveVar } from "@apollo/client";
import ErrorService from "../../../services/ErrorService";
import {
    useDeleteMenuMutation,
    useGetLanguageVersionsQuery,
    useGetMenuListQuery,
    useGetMenuTypesQuery,
} from "@me-team/host/main/menu/graphql/menu.hooks";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { useNavigate, useParams } from "react-router-dom";
import { BadgeAndButtonForArchive, Pagination, RenderEmptyList, Spinner } from "@services/ui-components";
import { ErrorModalComponent } from "@me-pos/error-modal";
import i18next from "i18next";
import { useToast } from "../../../hooks/useToast";
import ToastComponent from "../../ui-components/ToastComponent/ToastComponent";
import { LanguageVersions, MenuModalState, PartialMenu } from "../../../interfaces/meMenuInterfaces/interfaces";
import emptyImage from "../../../images/additives-empty-page.png";

import LabelStatusMenu from "./components/LabelStatusMenu";
import AddEditMenuModal from "./modals/AddEditMenuModal/AddEditMenuModal";
import { createMenuModalHelpers } from "./helpers";
import { initialMenuModalState } from "./constants";
import { Option, OptionString } from "../../../interfaces/interfaces";
import { getLanguageVersionOptions, getMenuTypeEnumOptions } from "./options";
import ConfirmDeleteModal from "./modals/ConfirmDeleteModal/ConfirmDeleteModal";
import { cloneNameToDisplay } from "../utils/cloneNameUtils";
import useScrollToTopOnChange from "../../../hooks/useScrollToTopOnChange";
import { canEditMenuContentByStatus, isMenuActivated } from "./menuEnumMappings";
import ConfirmPublishMenuModal from "./modals/ConfirmPublishMenuModal/ConfirmPublishMenuModal";
import { useActiveMenusByType } from "./hooks/useActiveMenusByType";
import ConfirmReplaceMenuModal from "./modals/ConfirmReplaceMenuModal/ConfirmReplaceMenuModal";
import { useMenuStatusActions } from "./hooks/useMenuStatusActions";
import { CompanyAccess } from "@me-team/host/main/graphql/types";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";

interface AllMenusTableProps {
    filters: {
        search: string;
        menuTypeId: number;
    };
    showAddMenuModal?: (fn: () => void) => void;
    onMenusLoaded?: (menus: PartialMenu[]) => void;
    onRefetchReady?: (refetch: () => void) => void;
}

const AllMenusTable: React.FC<AllMenusTableProps> = ({ filters, showAddMenuModal, onMenusLoaded, onRefetchReady }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { branchId } = useParams();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(20);
    const [menuModalState, setMenuModalState] = useState<MenuModalState>(initialMenuModalState);
    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
    const [menuForDelete, setMenuForDelete] = useState<PartialMenu>(null);
    const { showToast, toastText, toggleShowToast, setShowToast } = useToast();
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const canCreateMenu = useRoleAccess(CompanyAccess.MenuCreate);
    const canCloneEditMenu = useRoleAccess(CompanyAccess.MenuCloneEdit);
    const canDeleteRestoreMenu = useRoleAccess(CompanyAccess.MenuDeleteRestore);
    const canOpenDishCategories = useRoleAccess(CompanyAccess.MenuDishCategoryListView);
    const canOpenDishes = useRoleAccess(CompanyAccess.MenuDishListView);
    const canShowMenuActionsDropdown = canCloneEditMenu || canDeleteRestoreMenu;

    const isFilterNotEmpty = Object.values(filters).some((value) => value !== "" && value !== null);
    const { openCreateMenuModal, openEditMenuModal, openCloneMenuModal, openRestoreMenuModal, closeMenuModal } =
        useMemo(() => createMenuModalHelpers(setMenuModalState), []);
    useScrollToTopOnChange(currentPage);

    const handleCloseConfirmDeleteModal = () => {
        setShowConfirmDeleteModal(false);
        setMenuForDelete(null);
    };

    const { activeMenusByTypeRefetch } = useActiveMenusByType();
    const { publishMenu, replaceMenu, menuAction, setMenuAction, loading } = useMenuStatusActions({
        branchId: Number(branchId),
        t,
        showToast: toggleShowToast,
        onSuccess: async () => {
            await activeMenusByTypeRefetch();
            await menuListRefetch();
            setMenuAction(null);
        },
        onActiveMenuError: (menu) => {
            setMenuAction({ type: "replace", menu });
        },
    });

    const handleConfirmPublish = async () => {
        if (!canCloneEditMenu) return;
        if (!menuAction || menuAction.type !== "publish" || isSubmitting) return;
        try {
            setIsSubmitting(true);
            await publishMenu(menuAction.menu);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleConfirmReplace = async () => {
        if (!canCloneEditMenu) return;
        if (!menuAction || menuAction.type !== "replace" || isSubmitting) return;
        try {
            setIsSubmitting(true);
            await replaceMenu(menuAction.menu);
            navigate(`/menu/${menuAction.menu.id}/before-publish/branch/${branchId}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    const [deleteMenu] = useDeleteMenuMutation();

    const {
        data: menuListData,
        loading: menuListLoading,
        refetch: menuListRefetch,
    } = useGetMenuListQuery({
        variables: {
            companyId: Number(currentCompanyId),
            branchId: Number(branchId),
            page: currentPage,
            itemsPerPage: itemsPerPage,
            search: filters?.search ? filters.search : null,
            menuTypeId: filters?.menuTypeId ? filters.menuTypeId : null,
        },
        skip: !currentCompanyId,
        context: {
            errorType: "global",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
        fetchPolicy: "network-only",
    });

    const menuListPaginated = menuListData?.user?.company?.branchesPaginated?.branches?.[0]?.menus;
    const menuList: PartialMenu[] = menuListPaginated?.menus;
    const pageCount = parseInt(menuListPaginated?.pageCount || "0");
    const totalItems = menuListPaginated?.totalCount || 0;
    const firstItemIndex = (currentPage - 1) * itemsPerPage + 1;
    const lastItemIndex = Math.min(currentPage * itemsPerPage, totalItems);

    const { data: menuTypesData, loading: menuTypesLoading } = useGetMenuTypesQuery({
        context: {
            errorType: "local",
        },
        onError: (error: ApolloError) => {
            console.error("error", error);
        },
    });
    const menuTypeOptions: OptionString[] = getMenuTypeEnumOptions(menuTypesData?.menuTypes, (name) => t(name));

    const { data: languageVersionsData, loading: languageVersionsLoading } = useGetLanguageVersionsQuery({
        variables: { companyId: Number(currentCompanyId) },
        skip: !currentCompanyId,
        context: {
            errorType: "local",
        },
        onError: (error: ApolloError) => {
            console.error("error", error);
        },
    });
    const languageVersionOptions: Option[] = getLanguageVersionOptions(languageVersionsData?.user?.company?.languages);
    const primaryLanguage: LanguageVersions = languageVersionsData?.user?.company?.primaryLanguage;

    const handleDeleteMenu = async () => {
        if (!canDeleteRestoreMenu) return;
        if (!menuForDelete) return;

        await deleteMenu({
            variables: { id: menuForDelete.id, branchId: Number(branchId) },
            onCompleted: async () => {
                setMenuForDelete(null);
                setShowConfirmDeleteModal(false);
                await menuListRefetch();
                await activeMenusByTypeRefetch();
                toggleShowToast(t("Deleted"));
            },
            onError: (error) => {
                console.error("Error:", error);
            },
        });
    };

    React.useEffect(() => {
        showAddMenuModal?.(() => openCreateMenuModal);
    }, [showAddMenuModal, openCreateMenuModal]);

    useEffect(() => {
        if (onMenusLoaded && menuList) {
            onMenusLoaded(menuList);
        }
    }, [menuList, onMenusLoaded]);

    useEffect(() => {
        if (onRefetchReady) {
            onRefetchReady(() => menuListRefetch());
        }
    }, [onRefetchReady, menuListRefetch]);

    useEffect(() => {
        setCurrentPage(1);
    }, [filters?.search, filters?.menuTypeId]);

    const currentActiveMenu = menuList?.find(
        (m) => m.type?.id === menuAction?.menu?.type?.id && m.status?.slug === "activated"
    );

    if (menuListLoading || !currentCompanyId || menuTypesLoading || languageVersionsLoading) return <Spinner />;

    return (
        <>
            <div className="table-responsive mb-4 scrollbar">
                {!!menuList?.length ? (
                    <Table>
                        <thead>
                            <tr className="fs-7">
                                <th className="ps-4">{t("name")}</th>
                                <th>
                                    {t("type")}{" "}
                                    <OverlayTrigger
                                        key={"bottom"}
                                        placement={"bottom"}
                                        overlay={
                                            <Popover id="popover-contained">
                                                <Popover.Body>
                                                    <p className="m-0">
                                                        <span className="fw-bold">{t("Dine-In menu")}</span> -{" "}
                                                        {t("for offline service at the venue.")}
                                                    </p>
                                                    <p className="m-0">
                                                        <span className="fw-bold">{t("Pickup menu")}</span> -{" "}
                                                        {t("for online orders that customers pick up themselves.")}
                                                    </p>
                                                    <p className="m-0">
                                                        <span className="fw-bold">{t("Delivery menu")}</span> -{" "}
                                                        {t("for online orders with delivery.")}
                                                    </p>
                                                </Popover.Body>
                                            </Popover>
                                        }
                                    >
                                        <i className="bi bi-info-circle"></i>
                                    </OverlayTrigger>
                                </th>
                                <th className="text-nowrap">{t("Language versions")}</th>
                                <th className="text-nowrap">{t("Main language")}</th>
                                <th>
                                    {t("Status")}{" "}
                                    <OverlayTrigger
                                        key={"bottom"}
                                        placement={"bottom"}
                                        overlay={
                                            <Popover id="popover-contained">
                                                <Popover.Body>
                                                    <p className="m-0">
                                                        <span className="fw-bold">{t("Published")}</span> -{" "}
                                                        {t("the menu is available to customers via QR code or link.")}
                                                    </p>
                                                    <p className="m-0">
                                                        <span className="fw-bold">{t("hidden")}</span> -{" "}
                                                        {t(
                                                            "the menu is not visible to guests but remains available for editing."
                                                        )}
                                                    </p>
                                                    <p className="m-0">
                                                        <span className="fw-bold">{t("In development")}</span> -{" "}
                                                        {t("the menu is being created and has not been published yet.")}
                                                    </p>
                                                    <p className="m-0">
                                                        <span className="fw-bold">{t("archive")}</span> -{" "}
                                                        {t("the menu is stored in the archive and is not in use.")}
                                                    </p>
                                                </Popover.Body>
                                            </Popover>
                                        }
                                    >
                                        <i className="bi bi-info-circle"></i>
                                    </OverlayTrigger>
                                </th>
                                <th>{t("Categories")}</th>
                                <th>{t("Dishes")}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {menuList.map((menu) => {
                                const isArchive = menu?.deleted;
                                const colorForArchive = isArchive ? "text-secondary" : "text-dark";
                                const canOpenMenuContent = canEditMenuContentByStatus(
                                    menu.status?.slug,
                                    canCreateMenu,
                                    canCloneEditMenu
                                );
                                return (
                                    <tr key={menu.id}>
                                        <td className="ps-4 align-middle">
                                            <div className="d-flex flex-nowrap">
                                                <span className={isArchive ? "text-secondary" : "text-primary"}>
                                                    {cloneNameToDisplay(menu.name, t)}
                                                </span>
                                                {!isArchive && canOpenMenuContent && (
                                                    <OverlayTrigger overlay={<Tooltip>{t("Edit")}</Tooltip>}>
                                                        <Button
                                                            variant="link"
                                                            className="p-0 ms-2"
                                                            onClick={() => openEditMenuModal(menu)}
                                                        >
                                                            <i className="bi bi-pencil" aria-hidden="true"></i>
                                                        </Button>
                                                    </OverlayTrigger>
                                                )}
                                            </div>
                                        </td>
                                        <td className={`align-middle ${colorForArchive}`}>
                                            <span>{t(menu.type.name)}</span>
                                        </td>
                                        <td className={`align-middle ${colorForArchive}`}>
                                            {menu.languages?.map((lang) => lang.name).join(", ")}
                                        </td>
                                        <td className={`align-middle ${colorForArchive}`}>
                                            {menu.primaryLanguage?.name}
                                        </td>
                                        <td className="align-middle">
                                            <LabelStatusMenu status={menu.status?.slug} />
                                        </td>
                                        <td className="align-middle">
                                            <span className={isArchive || !canOpenDishCategories ? "text-secondary" : "text-dark"}>
                                                {menu.numberOfCategories}
                                            </span>
                                        </td>
                                        <td className="align-middle">
                                            <span className={isArchive || !canOpenDishes ? "text-secondary" : "text-dark"}>
                                                {menu.numberOfItems}
                                            </span>
                                        </td>
                                        <td className="align-middle py-3">
                                            <div className="d-flex gap-2 justify-content-end align-items-center">
                                                {!isArchive ? (
                                                    <>
                                                        {canOpenMenuContent && (
                                                            <OverlayTrigger overlay={<Tooltip>{t("Edit")}</Tooltip>}>
                                                                <Button
                                                                    variant="outline-secondary"
                                                                    className="py-2"
                                                                    onClick={() =>
                                                                        navigate(
                                                                            `/menu/page/${menu.id}/branch/${branchId}`
                                                                        )
                                                                    }
                                                                >
                                                                    <i className="bi bi-pencil text-dark"></i>
                                                                </Button>
                                                            </OverlayTrigger>
                                                        )}
                                                        <OverlayTrigger overlay={<Tooltip>{t("View")}</Tooltip>}>
                                                            <Button
                                                                variant="outline-secondary"
                                                                className="py-2"
                                                                onClick={() =>
                                                                    navigate(
                                                                        `/menu/${menu.id}/preview/branch/${branchId}`
                                                                    )
                                                                }
                                                            >
                                                                <i className="bi bi-eye text-dark"></i>
                                                            </Button>
                                                        </OverlayTrigger>
                                                        {canShowMenuActionsDropdown && (
                                                            <Dropdown
                                                                align="end"
                                                                id={`dropdown-${menu.id}`}
                                                                className="dropdown-menu-table"
                                                            >
                                                                <Dropdown.Toggle
                                                                    variant="outline-secondary"
                                                                    className="py-2"
                                                                >
                                                                    <i className="bi bi-three-dots text-dark"></i>
                                                                </Dropdown.Toggle>
                                                                {createPortal(
                                                                    <Dropdown.Menu>
                                                                        {canCloneEditMenu && (
                                                                            <Dropdown.Item
                                                                                onClick={() => openCloneMenuModal(menu)}
                                                                                className="ps-4 mb-1"
                                                                            >
                                                                                <i className="bi bi-stickies me-2"></i>
                                                                                <span>{t("Clone")}</span>
                                                                            </Dropdown.Item>
                                                                        )}
                                                                        {canCloneEditMenu &&
                                                                            !isMenuActivated(menu.status?.slug) && (
                                                                                <Dropdown.Item
                                                                                    onClick={() =>
                                                                                        setMenuAction({
                                                                                            type: "publish",
                                                                                            menu,
                                                                                        })
                                                                                    }
                                                                                    className="ps-4 mb-1"
                                                                                >
                                                                                    <i className="bi bi-qr-code-scan me-2"></i>
                                                                                    <span>{t("Publish")}</span>
                                                                                </Dropdown.Item>
                                                                            )}
                                                                        {canDeleteRestoreMenu && (
                                                                            <Dropdown.Item
                                                                                onClick={() => {
                                                                                    setMenuForDelete(menu);
                                                                                    menu &&
                                                                                        setShowConfirmDeleteModal(true);
                                                                                }}
                                                                                className="ps-4 mb-1"
                                                                            >
                                                                                <i className="bi bi-trash3 me-2"></i>
                                                                                <span>{t("Archive menu")}</span>
                                                                            </Dropdown.Item>
                                                                        )}
                                                                    </Dropdown.Menu>,
                                                                    document.body
                                                                )}
                                                            </Dropdown>
                                                        )}
                                                    </>
                                                ) : (
                                                    <BadgeAndButtonForArchive
                                                        hideButton={!canDeleteRestoreMenu}
                                                        onButtonClick={() => openRestoreMenuModal(menu)}
                                                    />
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                ) : null}
            </div>

            {!!totalItems ? (
                <div className="d-flex justify-content-between align-items-center mt-4 mb-5">
                    <Pagination
                        t={t}
                        pages={Number(pageCount)}
                        currentPage={currentPage}
                        onPageChange={(page) => setCurrentPage(page)}
                        firstItemIndex={firstItemIndex}
                        lastItemIndex={lastItemIndex}
                        totalItems={totalItems}
                    />
                </div>
            ) : (
                <RenderEmptyList
                    isFilterNotEmpty={isFilterNotEmpty}
                    imageSrc={emptyImage}
                    altText="Dishes"
                    emptyFilterTitle="No data found for the given values"
                    emptyListTitle="You don't have any menu yet. Create your first menu."
                    buttonName={
                        canCreateMenu ? (
                            <span className="fw-bold">
                                <i className="bi bi-plus-lg fw-bold me-1 w-100"></i>
                                {t("Add Menu")}
                            </span>
                        ) : null
                    }
                    onClickButton={() => {
                        if (canCreateMenu) openCreateMenuModal();
                    }}
                />
            )}
            {menuModalState.isOpen && (
                <AddEditMenuModal
                    show={menuModalState.isOpen}
                    mode={menuModalState.mode}
                    onClose={closeMenuModal}
                    menu={menuModalState.mode !== "create" && menuModalState?.menu}
                    languageVersionOptions={languageVersionOptions}
                    menuTypeOptions={menuTypeOptions}
                    primaryLanguage={primaryLanguage}
                    onRefetch={menuListRefetch}
                    onSuccessToast={(text) => toggleShowToast(text)}
                />
            )}

            {menuForDelete && showConfirmDeleteModal && (
                <ConfirmDeleteModal
                    show={showConfirmDeleteModal}
                    handleClose={handleCloseConfirmDeleteModal}
                    menuName={cloneNameToDisplay(menuForDelete?.name, t)}
                    onConfirm={handleDeleteMenu}
                />
            )}

            {menuAction?.type === "publish" && (
                <ConfirmPublishMenuModal
                    show={true}
                    onClose={() => setMenuAction(null)}
                    onConfirm={handleConfirmPublish}
                    menuTypeName={t(menuAction.menu?.type?.name)}
                    loading={loading || isSubmitting}
                />
            )}

            {menuAction?.type === "replace" && (
                <ConfirmReplaceMenuModal
                    show={true}
                    onClose={() => setMenuAction(null)}
                    onConfirm={handleConfirmReplace}
                    menuTypeName={t(menuAction.menu?.type?.name)}
                    menuName={cloneNameToDisplay(currentActiveMenu?.name, t)}
                    loading={loading || isSubmitting}
                />
            )}

            {showToast && <ToastComponent show={showToast} setShow={setShowToast} text={toastText} />}
            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
        </>
    );
};

export default AllMenusTable;
